import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import type { ThreadDetail } from '@rundown/types';
import { EmptyState } from '../../components';
import { avatarColour, initialOf } from '../../lib/avatar';
import { cx } from '../../lib/cx';
import { formatDateTime } from '../../lib/format';
import { queryKeys } from '../../lib/queryKeys';
import { AttachmentStrip } from '../attachments/AttachmentStrip';
import { attachmentsQueryOptions } from '../attachments/api';
import { type ComposerHandle } from '../composer/Composer';
import { MessageBodyView } from '../composer/MessageBodyView';
import { inlineAttachmentIds } from '../composer/imageNode';
import { Conversation, RequestView } from '../requests/RequestDetailPage';
import { requestQueryOptions } from '../requests/api';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { deleteThread, editThread, keepAsChat, markThreadRead, threadQueryOptions } from './api';
import { EditedMark, MessageActions, MessageEditor } from './MessageControls';
import { useCapability } from '../workspace/useCapability';
import { BotThinking } from './BotMessage';
import { KindBadge } from './ThreadSidebar';

/**
 * One thread, open.
 *
 * A chat is a conversation and nothing else: its opening post, the replies,
 * a composer. An issue is the request screen as it has always been — fields,
 * description, progress, and the same conversation beside them — because
 * "the view as we currently see it" is exactly what an issue should be.
 * When @rundown converts a chat, the realtime event refetches this thread
 * and the pane changes shape under the reader.
 */
export function ThreadPane() {
  const { workspace } = useRequiredWorkspace();
  const { threadId = '' } = useParams();
  const queryClient = useQueryClient();

  const query = useQuery({ ...threadQueryOptions(workspace.id, threadId), enabled: threadId !== '' });
  const thread = query.data;

  // Read on open, and again whenever something new lands while it is open —
  // a thread you are looking at is a thread you have read.
  const read = useMutation({
    mutationFn: () => markThreadRead(workspace.id, threadId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.threads(workspace.id) }),
  });
  const readMutate = read.mutate;
  const lastActivityAt = thread?.lastActivityAt;
  useEffect(() => {
    if (lastActivityAt !== undefined) readMutate();
  }, [threadId, lastActivityAt, readMutate]);

  if (query.isPending) return <p className="p-8 fs-13 text-muted">Loading…</p>;
  if (query.error !== null || thread === undefined) {
    return (
      <EmptyState
        title="This thread isn't available"
        description={query.error?.message ?? 'It may have been removed.'}
      />
    );
  }

  return thread.kind === 'issue' ? <IssueThread thread={thread} /> : <ChatThread thread={thread} />;
}

function BackToThreads() {
  return (
    <Link to="/" className="mb-2 inline-block flex-none self-start fs-12 text-muted hover:text-ink md:hidden">
      ← Threads
    </Link>
  );
}

function IssueThread({ thread }: { thread: ThreadDetail }) {
  const { workspace } = useRequiredWorkspace();
  const isAdmin = useCapability('workspace.admin');
  const request = useQuery(requestQueryOptions(workspace.id, thread.id));

  if (request.isPending) return <p className="p-8 fs-13 text-muted">Loading…</p>;
  if (request.error !== null || request.data === undefined) {
    return <EmptyState title="Could not load this issue" description={request.error?.message ?? ''} />;
  }

  return (
    <RequestView
      request={request.data}
      inThread
      conversationFooter={<BotThinking workspaceId={workspace.id} requestId={thread.id} />}
      actions={
        thread.canKeepAsChat || (thread.canDelete && !isAdmin) ? (
          <span className="flex items-center gap-2">
            {thread.canKeepAsChat && <KeepAsChatButton threadId={thread.id} />}
            {/* Admins delete issues from the header menu, with the number
                typed as confirmation; this is the author's "posted by
                mistake" on an issue nobody has started. */}
            {thread.canDelete && !isAdmin && <DeleteThreadButton threadId={thread.id} />}
          </span>
        ) : undefined
      }
    />
  );
}

/** Delete the whole thread, confirmed in place. Lands back on Home. */
function DeleteThreadButton({ threadId }: { threadId: string }) {
  const { workspace } = useRequiredWorkspace();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => deleteThread(workspace.id, threadId),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: queryKeys.thread(workspace.id, threadId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.threads(workspace.id) });
      void navigate('/');
    },
  });

  if (confirming) {
    return (
      <span className="flex items-center gap-1.5 rounded-lg bg-danger-tint px-2 py-1 fs-11.5">
        <span className="text-danger">{remove.error?.message ?? 'Delete this thread for everyone?'}</span>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="px-1 text-ink-soft hover:text-ink"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => remove.mutate()}
          disabled={remove.isPending}
          className="rounded-md bg-danger px-2 py-0.5 font-semibold text-white disabled:opacity-50"
        >
          {remove.isPending ? 'Deleting…' : 'Delete'}
        </button>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 fs-11.5 font-semibold text-ink-soft hover:border-danger hover:text-danger"
    >
      <Trash2 size={12} strokeWidth={2} aria-hidden />
      Delete
    </button>
  );
}

function KeepAsChatButton({ threadId, className }: { threadId: string; className?: string }) {
  const { workspace } = useRequiredWorkspace();
  const queryClient = useQueryClient();
  const keep = useMutation({
    mutationFn: () => keepAsChat(workspace.id, threadId),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.thread(workspace.id, threadId), next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.threads(workspace.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.requestMessages(workspace.id, threadId) });
    },
  });

  return (
    <span className={cx('flex items-center gap-2', className)}>
      {keep.error !== null && <span className="fs-11 text-danger">{keep.error.message}</span>}
      <button
        type="button"
        onClick={() => keep.mutate()}
        disabled={keep.isPending}
        title="Not work after all — take it off the backlog and keep talking"
        className="rounded-lg border border-line px-2.5 py-1 fs-11.5 font-semibold text-ink-soft hover:bg-surface-2 hover:text-ink disabled:opacity-50"
      >
        {keep.isPending ? 'Keeping as chat…' : 'Keep as chat'}
      </button>
    </span>
  );
}

function ChatThread({ thread }: { thread: ThreadDetail }) {
  const { workspace } = useRequiredWorkspace();
  const composerRef = useRef<ComposerHandle>(null);
  const internal = thread.visibleToClient === false;

  return (
    <div className="animate-enter flex min-h-0 flex-1 flex-col px-5 pt-3 pb-5 md:px-8">
      <BackToThreads />
      <div className="mb-3 flex flex-none flex-wrap items-center gap-2">
        <KindBadge thread={thread} />
        {internal && (
          <span className="flex items-center gap-1 rounded-full bg-violet-tint px-2 py-0.5 fs-11 font-semibold text-violet">
            <Lock size={11} strokeWidth={2.25} aria-hidden />
            Internal
          </span>
        )}
        <h1 className="min-w-0 flex-1 truncate fs-17 font-bold tracking-[-0.01em] text-ink">
          {thread.title}
        </h1>
        {thread.canDelete && <DeleteThreadButton threadId={thread.id} />}
      </div>

      <div className="mx-auto flex min-h-0 w-full max-w-[56rem] flex-1 flex-col overflow-hidden rounded-xl border border-line">
        <Conversation
          workspaceId={workspace.id}
          requestId={thread.id}
          composerRef={composerRef}
          placeholder="Reply, or mention @rundown to ask it something. ⌘↵ to post."
          opening={<OpeningPost thread={thread} />}
          footer={<BotThinking workspaceId={workspace.id} requestId={thread.id} />}
        />
      </div>
    </div>
  );
}

/**
 * The first message of a chat — which is the request's description, stored
 * where a request's opening words have always been stored, and drawn here as
 * the first bubble so a chat reads like one.
 */
function OpeningPost({ thread }: { thread: ThreadDetail }) {
  const { workspace } = useRequiredWorkspace();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const edit = useMutation({
    mutationFn: (body: unknown) => editThread(workspace.id, thread.id, body),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.thread(workspace.id, thread.id), next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.threads(workspace.id) });
      setEditing(false);
    },
  });
  const attachments = useQuery(attachmentsQueryOptions(workspace.id, thread.id)).data?.items ?? [];
  const who = thread.requesterName ?? 'Unknown';
  const inline = inlineAttachmentIds(thread.description);
  const own = attachments.filter((item) => item.messageId === null && !inline.has(item.id));

  return (
    <div className="group flex gap-2.5">
      <div
        className={cx(
          'grid size-[1.625rem] flex-none place-items-center rounded-full fs-11 font-bold text-white',
          avatarColour(who),
        )}
        aria-hidden
      >
        {initialOf(who)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="flex-none fs-12 font-bold whitespace-nowrap">{who}</span>
          <span className="fs-11 whitespace-nowrap text-muted">{formatDateTime(thread.createdAt)}</span>
          <EditedMark at={thread.descriptionEditedAt} />
          {!editing && (
            // Deleting the opening post is deleting the thread — that button
            // is in the header, where it says so.
            <MessageActions
              canEdit={thread.canEdit}
              canDelete={false}
              onEdit={() => setEditing(true)}
              onDelete={() => undefined}
            />
          )}
        </div>
        {editing && (
          <MessageEditor
            workspaceId={workspace.id}
            initial={thread.description ?? thread.descriptionText}
            pending={edit.isPending}
            error={edit.error}
            onSave={(body) => edit.mutate(body)}
            onCancel={() => setEditing(false)}
          />
        )}
        {!editing && thread.descriptionText.trim() !== '' && (
          <MessageBodyView
            body={thread.description}
            fallbackText={thread.descriptionText}
            resolveAttachmentUrl={(id) => attachments.find((item) => item.id === id)?.url}
          />
        )}
        <AttachmentStrip attachments={own} className="mt-1.5" />
      </div>
    </div>
  );
}
